import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory, generatePath } from 'react-router';
import { useTheme } from '@emotion/react';
import { getUserToken } from '@/redux/user';
import {
    useDeleteWebhookMutation,
    useGetWebhookQuery,
    usePutWebhookMutation,
} from '@/services/innovorder/moduleExernalWebhooks/moduleExternalWebhooks.endpoints';
import { Routes } from '@/core/router/routes.types';
import {
    WebhookConfig,
    FormRefType,
    buildEventTypeString,
    resetFormWithWebhookData,
} from '@/pages/Config/Webhooks/types';
import { EditBrandWebhookViewModel } from './types';

export const useEditBrandWebhookVM = (): EditBrandWebhookViewModel => {
    const theme = useTheme();
    const history = useHistory();
    const formRef = useRef<FormRefType>();
    const [isValid, setValid] = useState(false);
    const [isDirty, setDirty] = useState(false);
    const [isDelete, setDelete] = useState(false);
    const token = useSelector(getUserToken);
    const { webhookId, brandId } = useParams<{
        webhookId: string;
        brandId: string;
    }>();
    const { data: externalWebhook, isLoading: isDataLoading } = useGetWebhookQuery({
        token,
        webhookId,
    });

    const [putWebhook, { isLoading: isPutLoading, error: putError }] = usePutWebhookMutation();
    const [deleteWebhook, { isLoading: isDeleteLoading, error: deleteError }] = useDeleteWebhookMutation();

    const onSubmit = useCallback(
        async (data: WebhookConfig) => {
            const eventTypeString = buildEventTypeString(data.eventTypes);

            const updateResult = await putWebhook({
                token,
                webhookId,
                body: {
                    brandId: Number(brandId),
                    webhookUrl: data.webhookUrl,
                    channels: data.channels,
                    eventType: eventTypeString,
                    additionalHeaders: JSON.stringify(data.additionalHeaders),
                },
            }).unwrap();

            // Reset form with updated data
            formRef.current?.reset(resetFormWithWebhookData(updateResult, data.channels));
        },
        [brandId, putWebhook, token, webhookId],
    );

    const onDelete = useCallback(async () => {
        const res = await deleteWebhook({ token, webhookId }).unwrap();
        if (res) {
            history.push(generatePath(Routes.Integrations, { brandId }));
        }
    }, [token, webhookId, brandId, deleteWebhook, history]);

    const onSave = useCallback(() => {
        formRef.current?.handleSubmit(onSubmit)();
    }, [onSubmit]);

    const shouldRedirect = !isDataLoading && !externalWebhook;

    return {
        theme,
        formRef,
        isValid,
        setValid,
        isDirty,
        setDirty,
        isDelete,
        setDelete,
        externalWebhook,
        isDataLoading,
        isPutLoading,
        putError,
        isDeleteLoading,
        deleteError,
        onSubmit,
        onDelete,
        onSave,
        shouldRedirect,
    };
};
